import { useState, useEffect, useRef, useCallback, useMemo, FormEvent, ReactElement } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Account, Currency as CurrencyType, TransactionCategory, TransactionCategoryDetail, TransactionDetailValue, TransactionCategoryGroup, Transaction, TransactionCustomFieldValues } from "../types";
import { api } from "../services/api";
import RefreshIconButton from "../components/RefreshIconButton";
import { convertFormattedAmountToRawValue, getCurrencyDecimalPlaces } from "../utils/currencyDefaults";
import { formatCurrency as formatCurrencyUtil, getTransactionFriendlyId, copyToClipboard } from "../utils/transactionUtils";
import CopyFeedbackTooltip from "../components/CopyFeedbackTooltip";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
// Mobile hooks
import { useCurrencyHelpers } from "./Activity/hooks/useCurrencyHelpers";
import { useMobileDataFetching } from "./Activity/mobile/hooks/useMobileDataFetching";
import { useMobileSessionStorage } from "./Activity/mobile/hooks/useMobileSessionStorage";
import { useMobileFieldFocus, type FocusedField } from "./Activity/mobile/hooks/useMobileFieldFocus";
import { useMobileRateCalculations } from "./Activity/mobile/hooks/useMobileRateCalculations";
import { useMobileCategoryDetails } from "./Activity/mobile/hooks/useMobileCategoryDetails";
import { useMobileFormUtils, getFilteredAccounts, getCurrentCategories } from "./Activity/mobile/hooks/useMobileFormUtils";
import { useMobileCalculator } from "./Activity/mobile/hooks/useMobileCalculator";
import { useMobileFormHandlers } from "./Activity/mobile/hooks/useMobileFormHandlers";
import { useMobileTransactionSubmission } from "./Activity/mobile/hooks/useMobileTransactionSubmission";
import { useMobileCalendar } from "./Activity/mobile/hooks/useMobileCalendar";
import { useMobileCalculatorHandler } from "./Activity/mobile/hooks/useMobileCalculatorHandler";
import { useMobileTimePickerHandlers } from "./Activity/mobile/hooks/useMobileTimePickerHandlers";
import { useMobileEditMode } from "./Activity/mobile/hooks/useMobileEditMode";
import { useMobileFieldFocusEffects } from "./Activity/mobile/hooks/useMobileFieldFocusEffects";
import { useMobileTimePickerEffects } from "./Activity/mobile/hooks/useMobileTimePickerEffects";
import { useMobileStateRestoration } from "./Activity/mobile/hooks/useMobileStateRestoration";
import { useMobileNavigation } from "./Activity/mobile/hooks/useMobileNavigation";
import { mergeNoteAndAttachmentIntoCustomFieldValues } from "../utils/transactionCustomFieldsDerivation";
import { useWorkspaceSettings } from "./WorkspaceSettings/hooks/useWorkspaceSettings";
import { useTransactionCustomFields } from "./Activity/hooks/useTransactionCustomFields";
// Mobile components
import MobileTimePicker from "./Activity/mobile/components/MobileTimePicker";
import MobileDatePicker from "./Activity/mobile/components/MobileDatePicker";
import MobileCalculator from "./Activity/mobile/components/MobileCalculator";
import MobileBottomPanel from "./Activity/mobile/components/MobileBottomPanel";
import MobileTransactionFormFields from "./Activity/mobile/components/MobileTransactionFormFields";
import MobileTransactionHeader from "./Activity/mobile/components/MobileTransactionHeader";
import MobileTransactionSubmitButton from "./Activity/mobile/components/MobileTransactionSubmitButton";
import MobileAddOptionDialog from "./Activity/mobile/components/MobileAddOptionDialog";
import MobileFeeEditDialog from "./Activity/mobile/components/MobileFeeEditDialog";
import { FeeEditFormData } from "./Activity/components/FeeEditDialog";
import { useFeeSubmission } from "./Activity/hooks/useFeeSubmission";
import { TransactionWithDate } from "../utils/transactionProcessing";
import { Timestamp } from "firebase/firestore";

interface CreateTransactionMobileProps {
  workspaceId: string;
}

type TransactionType = "expense" | "income" | "transfer";

interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

function CreateTransactionMobile({ workspaceId }: CreateTransactionMobileProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ workspaceSlug?: string; type?: string; transactionId?: string; "*"?: string }>();
  const workspaceSlug = params.workspaceSlug;
  // App route is /:workspaceSlug/* so type/transactionId are in the splat, not named params
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isEditRoute = pathSegments[1] === "edit-transaction";
  const isCreateOrEditTransaction = pathSegments[1] === "create-transaction" || isEditRoute;
  const type = isCreateOrEditTransaction ? (pathSegments[2] || params.type || "expense") : (params.type || "expense");
  const transactionId = isEditRoute ? (pathSegments[3] || params.transactionId) : params.transactionId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Detect iOS Safari for native date/time picker support
  const isIOS = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
    return isIOSDevice && isSafari;
  }, []);

  // Get workspace settings for time format, date format, and transaction ID prefix
  const { timeFormat, dateFormat, transactionIdPrefix, transactionIdNextSequence, allowExpenseFee, allowTransferFee } = useWorkspaceSettings({ workspaceId });

  // Use mobile form utils hook (dateFormat so date field display respects workspace setting)
  const {
    formatCurrency,
    formatDateLocal,
    formatDateForDisplay,
    formatTimeLocal,
    getCurrentTime,
    combineDateAndTime,
  } = useMobileFormUtils(dateFormat);

  const transactionType: TransactionType = (type as TransactionType) || "expense";
  
  // Type guard helper
  const isExpenseOrIncome = (t: TransactionType): t is "expense" | "income" => {
    return t === "expense" || t === "income";
  };

  // Storage key for preserving transaction form state
  const storageKey = `transaction-form-${workspaceId}-${transactionType}`;
  // Storage key for tracking if user was on transaction page (to auto-navigate back)
  const routeStorageKey = `transaction-route-${workspaceId}`;

  // getSavedState and clearSavedState are now provided by useMobileSessionStorage hook

  // Use mobile data fetching hook
  const {
    accounts,
    currencies,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    loading,
    workspaceMembersCount,
    transactionIdPrefix: dataFetchingTransactionIdPrefix,
    transactionIdNextSequence: dataFetchingTransactionIdNextSequence,
  } = useMobileDataFetching({
    workspaceId,
    transactionType,
  });

  // Same backend as desktop: useCurrencyHelpers for rate calculation
  const { calculateTransferRate: calculateTransferRateFromHelpers } = useCurrencyHelpers(currencies, defaultCurrency);

  // State (formData, transferFormData, etc. are managed locally, not in hooks)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);
  // Track if user is currently interacting with optional fields (note/attachment)
  // This prevents auto-focus from triggering when user types in these fields
  const [isOnOptionalField, setIsOnOptionalField] = useState<boolean>(false);
  
  // Track calculator reset counter to force reset when user clicks on amount field
  const [calculatorResetKey, setCalculatorResetKey] = useState<number>(0);
  
  // Use mobile calculator hook
  const {
    calculatorExpression,
    setCalculatorExpression,
    hasEvaluatedRef,
    parseExpressionIntoCalculator,
    getCalculatorDisplayValue,
    evaluateExpression,
    handleCalculatorButton,
  } = useMobileCalculator();
  // isInitialFocus is now provided by useMobileFieldFocus hook

  // Calendar state
  const [calendarViewDate, setCalendarViewDate] = useState<Date>(new Date());

  // Exchange rate tracking
  const [rateManuallyEdited, setRateManuallyEdited] = useState<boolean>(false);
  const [defaultCurrencyRateManuallyEdited, setDefaultCurrencyRateManuallyEdited] = useState<boolean>(false);

  // Edit mode state
  const isEditMode = !!transactionId;
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null); // Track initial form data for change detection
  const [initialTransferFormData, setInitialTransferFormData] = useState<TransferFormData | null>(null); // For transfer edit change detection
  const [timeWasEnabled, setTimeWasEnabled] = useState<boolean>(false); // Track if time was enabled during creation
  const [keepTimePickerOpen, setKeepTimePickerOpen] = useState<boolean>(false); // Track if time picker should stay open when calendar opens
  const [showTransferFee, setShowTransferFee] = useState<boolean>(false); // Track if fee fields are shown
  
  // Fee edit dialog state
  const [feeEditDialogOpen, setFeeEditDialogOpen] = useState<boolean>(false);
  const [feeEditFormData, setFeeEditFormData] = useState<FeeEditFormData>({
    feeAmount: "",
    feeAccountId: "",
  });
  const [isSubmittingFee, setIsSubmittingFee] = useState<boolean>(false);
  const [feeEditCopyLastKey, setFeeEditCopyLastKey] = useState<string | null>(null);
  const feeEditCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFeeEditCopyFeedback = useCallback((key: string) => {
    if (feeEditCopyTimeoutRef.current) clearTimeout(feeEditCopyTimeoutRef.current);
    setFeeEditCopyLastKey(key);
    feeEditCopyTimeoutRef.current = setTimeout(() => {
      setFeeEditCopyLastKey(null);
      feeEditCopyTimeoutRef.current = null;
    }, 2000);
  }, []);
  useEffect(() => () => { if (feeEditCopyTimeoutRef.current) clearTimeout(feeEditCopyTimeoutRef.current); }, []);

  // Newly added detail options (temporary, not saved until transaction is created)
  // Maps detail name to array of newly added options
  const [newlyAddedDetailOptions, setNewlyAddedDetailOptions] = useState<Record<string, string[]>>({});
  
  // Dialog state for adding new detail options
  const [addOptionDialogOpen, setAddOptionDialogOpen] = useState<boolean>(false);
  const [addOptionDialogDetailName, setAddOptionDialogDetailName] = useState<string>("");
  const [addOptionDialogValue, setAddOptionDialogValue] = useState<string>("");
  const [addOptionDialogError, setAddOptionDialogError] = useState<string>("");
  
  // Track which detail lists are open
  const [openDetailLists, setOpenDetailLists] = useState<Record<string, boolean>>({});

  // Form data
  const [formData, setFormData] = useState<FormData>({
    category: "",
    amount: "",
    currency: "",
    rate: "",
    defaultCurrencyRate: "",
    date: formatDateLocal(new Date()),
    time: "",
    accountId: "",
    accountGroup: "",
    customFieldValues: undefined,
    customFieldPendingFiles: undefined,
  });

  const [transferFormData, setTransferFormData] = useState<TransferFormData>({
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    date: formatDateLocal(new Date()),
    time: "",
    transferRate: "",
    customFieldValues: undefined,
    customFieldPendingFiles: undefined,
  });

  const customFieldFileInputRef = useRef<HTMLInputElement>(null);
  const transferCustomFieldFileInputRef = useRef<HTMLInputElement>(null);
  const [centeredHour, setCenteredHour] = useState<string>("");
  const [centeredMinute, setCenteredMinute] = useState<string>("");

  const { fields: expenseCustomFields } = useTransactionCustomFields({ workspaceId, transactionType: "expense" });
  const { fields: incomeCustomFields } = useTransactionCustomFields({ workspaceId, transactionType: "income" });
  const { fields: transferCustomFields } = useTransactionCustomFields({ workspaceId, transactionType: "transfer" });
  
  // Ref to track if we're intentionally navigating back (to prevent saving route info on unmount)
  const isNavigatingBackRef = useRef<boolean>(false);
  
  // Ref to track current path for route info saving on unmount
  const currentPathRef = useRef(location.pathname);
  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  // Use mobile field focus hook FIRST (provides focusedField)
  const {
    focusedField,
    setFocusedField,
    handleFieldFocus,
    handleFieldBlur,
    bottomPanelRef,
    timePickerRef,
    amountInputRef,
    isInitialFocus,
    setIsInitialFocus,
  } = useMobileFieldFocus({
    transactionType,
    formData,
    transferFormData,
    calculatorExpression,
    hasEvaluatedRef,
    setCalculatorExpression,
    setFormData,
    setTransferFormData,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    setKeepTimePickerOpen,
    isOnOptionalField,
    setIsOnOptionalField,
    currencies,
    defaultCurrency,
    accounts,
  });

  // Use mobile category details hook (provides selectedCategoryDetails)
  const {
    selectedCategoryDetails,
    setSelectedCategoryDetails,
    deriveCategoryDetails,
    handleCategorySelect: hookHandleCategorySelect,
  } = useMobileCategoryDetails({
    transactionType,
    isEditMode,
    formData,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    setSelectedCategoryDetails: () => {}, // Hook manages this internally via its own state
    setFormData,
  });

  // Refs to store latest state values for saving (after hooks are initialized)
  const formDataRef = useRef(formData);
  const transferFormDataRef = useRef(transferFormData);
  const focusedFieldRef = useRef(focusedField);
  const calculatorExpressionRef = useRef(calculatorExpression);
  const addOptionDialogOpenRef = useRef(addOptionDialogOpen);
  const addOptionDialogDetailNameRef = useRef(addOptionDialogDetailName);
  const addOptionDialogValueRef = useRef(addOptionDialogValue);
  const newlyAddedDetailOptionsRef = useRef(newlyAddedDetailOptions);
  const openDetailListsRef = useRef(openDetailLists);
  const rateManuallyEditedRef = useRef(rateManuallyEdited);
  const selectedCategoryDetailsRef = useRef(selectedCategoryDetails);
  const formContentRef = useRef<HTMLDivElement>(null);

  // Update refs when state changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    transferFormDataRef.current = transferFormData;
  }, [transferFormData]);
  useEffect(() => {
    focusedFieldRef.current = focusedField;
  }, [focusedField]);

  // Scroll focused field into view above keyboard/calculator
  useEffect(() => {
    if (!focusedField) return;
    const scrollTarget = focusedField === "time" ? "date" : focusedField;
    const timer = window.setTimeout(() => {
      const el = formContentRef.current?.querySelector(`[data-field="${scrollTarget}"]`);
      (el as HTMLElement)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusedField]);

  useEffect(() => {
    calculatorExpressionRef.current = calculatorExpression;
  }, [calculatorExpression]);
  useEffect(() => {
    addOptionDialogOpenRef.current = addOptionDialogOpen;
  }, [addOptionDialogOpen]);
  useEffect(() => {
    addOptionDialogDetailNameRef.current = addOptionDialogDetailName;
  }, [addOptionDialogDetailName]);
  useEffect(() => {
    addOptionDialogValueRef.current = addOptionDialogValue;
  }, [addOptionDialogValue]);
  useEffect(() => {
    newlyAddedDetailOptionsRef.current = newlyAddedDetailOptions;
  }, [newlyAddedDetailOptions]);
  useEffect(() => {
    openDetailListsRef.current = openDetailLists;
  }, [openDetailLists]);
  useEffect(() => {
    rateManuallyEditedRef.current = rateManuallyEdited;
  }, [rateManuallyEdited]);
  useEffect(() => {
    selectedCategoryDetailsRef.current = selectedCategoryDetails;
  }, [selectedCategoryDetails]);

  // Use mobile session storage hook
  const {
    saveState,
    getSavedState,
    clearSavedState,
    hasRestoredStateRef,
  } = useMobileSessionStorage({
    workspaceId,
    transactionType,
    storageKey,
    routeStorageKey,
    formData,
    transferFormData,
    focusedField,
    calculatorExpression,
    addOptionDialogOpen,
    addOptionDialogDetailName,
    addOptionDialogValue,
    newlyAddedDetailOptions,
    openDetailLists,
    rateManuallyEdited,
    selectedCategoryDetails,
    isNavigatingBackRef,
  });

  // useMobileFieldFocus hook is called above, before session storage hook

  // Callbacks so refresh can update calculator when that field is focused (avoids stale display from bubbled click)
  const onRateUpdated = useCallback((newRate: string) => {
    if (focusedFieldRef.current === "rate") parseExpressionIntoCalculator(newRate);
  }, [parseExpressionIntoCalculator]);
  const onDefaultRateUpdated = useCallback((newRate: string) => {
    if (focusedFieldRef.current === "defaultCurrencyRate") parseExpressionIntoCalculator(newRate);
  }, [parseExpressionIntoCalculator]);

  // Use mobile rate calculations hook (same backend as desktop: formatRate + useCurrencyHelpers)
  const { getCurrencyRate, calculateTransferRate, handleRefreshRate, handleRefreshDefaultCurrencyRate } = useMobileRateCalculations({
    transactionType,
    formData,
    transferFormData,
    accounts,
    currencies,
    defaultCurrency,
    rateManuallyEdited,
    defaultCurrencyRateManuallyEdited,
    focusedField,
    isEditMode,
    editingTransaction,
    setFormData,
    setTransferFormData,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    calculateTransferRateIn: calculateTransferRateFromHelpers,
    onRateUpdated,
    onDefaultRateUpdated,
  });

  // useMobileCategoryDetails hook is called above, before session storage hook

  // Use mobile form handlers hook
  const {
    handleAccountSelect,
    handleCurrencySelect,
    handleCategorySelect,
    checkDuplicateOption,
    handleAddDetailOption,
    handleCloseAddOptionDialog,
  } = useMobileFormHandlers({
    transactionType,
    focusedField,
    setFocusedField,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    accounts,
    defaultCurrency,
    calculateTransferRate,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    hookHandleCategorySelect,
    selectedCategoryDetails,
    newlyAddedDetailOptions,
    setNewlyAddedDetailOptions,
    addOptionDialogValue,
    setAddOptionDialogValue,
    addOptionDialogDetailName,
    setAddOptionDialogDetailName,
    addOptionDialogError,
    setAddOptionDialogError,
    addOptionDialogOpen,
    setAddOptionDialogOpen,
  });

  // Use mobile transaction submission hook
  // Get returnUrl from location.state (set by CategoryGroupDetail when navigating to edit)
  const returnUrl = (location.state as any)?.returnUrl || null;
  
  const { handleSubmit, validateAndFocusFirstEmpty } = useMobileTransactionSubmission({
    workspaceId,
    workspaceSlug,
    transactionType,
    isEditMode,
    transactionId,
    formData,
    setFormData,
    transferFormData,
    accounts,
    currencies,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    newlyAddedDetailOptions,
    setNewlyAddedDetailOptions,
    focusedField,
    handleFieldFocus,
    combineDateAndTime,
    clearSavedState,
    routeStorageKey,
    isNavigatingBackRef,
    setIsSubmitting,
    setUploadingAttachment,
    navigate,
    defaultCurrencyRateManuallyEdited,
    transactionCustomFields: transactionType === "transfer" ? transferCustomFields : transactionType === "income" ? incomeCustomFields : expenseCustomFields,
    returnUrl,
    allowExpenseFee,
    allowTransferFee,
  });

  // Use mobile state restoration hook
  useMobileStateRestoration({
    transactionType,
    isEditMode,
    loading,
    accounts,
    categoryGroups,
    expenseCategories,
    incomeCategories,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    setSelectedCategoryDetails,
    setCalculatorExpression,
    setAddOptionDialogDetailName,
    setAddOptionDialogValue,
    setAddOptionDialogOpen,
    setFocusedField,
    setNewlyAddedDetailOptions,
    setOpenDetailLists,
    setRateManuallyEdited,
    getSavedState,
    hasRestoredStateRef,
    location,
  });

  // Use mobile navigation hook
  useMobileNavigation({
    transactionType,
    workspaceSlug,
    routeStorageKey,
    isNavigatingBackRef,
    location,
    navigate,
  });

  // Initialize currency to default currency when defaultCurrency is loaded or changes (for non-transfer transactions)
  // This ensures currency always matches the current defaultCurrency when form is first opened
  const prevDefaultCurrencyRef = useRef<string>("");
  
  useEffect(() => {
    if (transactionType !== "transfer" && defaultCurrency) {
      const prevDefault = prevDefaultCurrencyRef.current;
      
      // If currency is empty, set it to defaultCurrency
      if (!formData.currency) {
        setFormData((prev) => ({
          ...prev,
          currency: defaultCurrency,
        }));
        prevDefaultCurrencyRef.current = defaultCurrency;
      } 
      // If defaultCurrency changed and current currency matches the previous default, update it
      // This handles the case where user changed default currency while creating a transaction
      else if (prevDefault && formData.currency === prevDefault && formData.currency !== defaultCurrency) {
        setFormData((prev) => ({
          ...prev,
          currency: defaultCurrency,
        }));
        prevDefaultCurrencyRef.current = defaultCurrency;
      }
      // Initialize ref when defaultCurrency is first loaded
      if (!prevDefault) {
        prevDefaultCurrencyRef.current = defaultCurrency;
      }
      // Update ref when defaultCurrency changes
      else if (prevDefault !== defaultCurrency) {
        prevDefaultCurrencyRef.current = defaultCurrency;
      }
    }
  }, [defaultCurrency, transactionType, formData.currency]);
  
  // After state restoration, ensure currency matches defaultCurrency if it was restored from old session storage
  // This handles the case where user changed default currency and session storage has old currency value
  useEffect(() => {
    if (hasRestoredStateRef.current && defaultCurrency && transactionType !== "transfer") {
      // If currency doesn't match defaultCurrency, it was likely restored from old session storage
      // Update it to match the current defaultCurrency
      if (formData.currency && formData.currency !== defaultCurrency) {
        // Check if this currency matches the previous default (meaning it was auto-selected, not manually set)
        const prevDefault = prevDefaultCurrencyRef.current;
        if (formData.currency === prevDefault || !prevDefault) {
          // Currency was auto-selected (matches old default) or we don't have previous default yet
          // Update it to new defaultCurrency
          setFormData((prev) => ({
            ...prev,
            currency: defaultCurrency,
          }));
          prevDefaultCurrencyRef.current = defaultCurrency;
        }
      } else if (!formData.currency) {
        // Currency is empty after restoration, set it to defaultCurrency
        setFormData((prev) => ({
          ...prev,
          currency: defaultCurrency,
        }));
        prevDefaultCurrencyRef.current = defaultCurrency;
      }
    }
  }, [hasRestoredStateRef.current, defaultCurrency, formData.currency, transactionType]);

  // Calculate if form has changes (for edit mode) — disable Update until user changes something
  const hasTransactionChanges = useMemo(() => {
    if (!isEditMode) return true; // Create mode: button enabled (validation handles submit)

    // Edit mode: disable until we have loaded transaction and (for transfer) initial transfer data
    if (!editingTransaction) return false; // Still loading transaction

    if (editingTransaction.type === "transfer") {
      if (!initialTransferFormData) return false; // Still loading or no initial snapshot
      // Pending file upload counts as a change
      if (transferFormData.customFieldPendingFiles && Object.keys(transferFormData.customFieldPendingFiles).length > 0) return true;
      const normalizeAmount = (a: string | undefined) => (!a || a.trim() === "") ? "0" : (parseFloat(a) || 0).toString();
      const normalizeFee = (a: string | undefined) => {
        if (!a || a.trim() === "") return "";
        const n = parseFloat(a);
        return isNaN(n) ? "" : n.toString();
      };
      const normalizeRate = (r: string | undefined) => {
        if (!r || r.trim() === "") return "";
        const n = parseFloat(r);
        return isNaN(n) ? "" : n.toString();
      };
      const curAmount = normalizeAmount(transferFormData.amount);
      const initAmount = normalizeAmount(initialTransferFormData.amount);
      const curFee = normalizeFee(transferFormData.feeAmount);
      const initFee = normalizeFee(initialTransferFormData.feeAmount);
      const curRate = normalizeRate(transferFormData.transferRate);
      const initRate = normalizeRate(initialTransferFormData.transferRate);
      const curFeeAccount = transferFormData.feeAccountId || "";
      const initFeeAccount = initialTransferFormData.feeAccountId || "";
      return (
        transferFormData.fromAccountId !== initialTransferFormData.fromAccountId ||
        transferFormData.toAccountId !== initialTransferFormData.toAccountId ||
        curAmount !== initAmount ||
        transferFormData.date !== initialTransferFormData.date ||
        transferFormData.time !== initialTransferFormData.time ||
        curRate !== initRate ||
        curFee !== initFee ||
        curFeeAccount !== initFeeAccount
      );
    }

    // Expense/income: disable until initial form data is set (transaction loaded)
    if (!initialFormData) return false;

    // Pending file upload counts as a change
    if (formData.customFieldPendingFiles && Object.keys(formData.customFieldPendingFiles).length > 0) return true;

    // Normalize amount so "100" and "100.00" don't count as a change (calculator/formatting)
    const normAmount = (a: string) => (a === "" || !a.trim() ? 0 : parseFloat(a) || 0);
    const amountChanged = normAmount(formData.amount) !== normAmount(initialFormData.amount);
    // Normalize time (trim) so formatting differences don't count as change
    const curTime = (formData.time || "").trim();
    const initTime = (initialFormData.time || "").trim();
    // Compare rates numerically with tolerance to avoid false positives from formatting (e.g. "1.5" vs "1.5000")
    const ratesEqual = (a: string, b: string): boolean => {
      const na = (a ?? "").trim() ? parseFloat(a) : NaN;
      const nb = (b ?? "").trim() ? parseFloat(b) : NaN;
      if (isNaN(na) && isNaN(nb)) return true;
      if (isNaN(na) || isNaN(nb)) return false;
      return Math.abs(na - nb) < 1e-9;
    };
    const rateChanged = !ratesEqual(formData.rate, initialFormData.rate);
    const defaultRateChanged = !ratesEqual(formData.defaultCurrencyRate, initialFormData.defaultCurrencyRate);
    const categoryChanged = (formData.category || "").trim() !== (initialFormData.category || "").trim();
    const dateChanged = formData.date !== initialFormData.date;
    const timeChanged = curTime !== initTime;
    const accountChanged = formData.accountId !== initialFormData.accountId;
    const currencyChanged = formData.currency !== initialFormData.currency;
    const basicFieldsChanged = (
      categoryChanged ||
      amountChanged ||
      dateChanged ||
      timeChanged ||
      accountChanged ||
      currencyChanged ||
      rateChanged ||
      defaultRateChanged
    );
    if (basicFieldsChanged) return true;

    const currentDetails = formData.details || {};
    const initialDetails = initialFormData.details || {};
    const allDetailKeys = Array.from(new Set([...Object.keys(currentDetails), ...Object.keys(initialDetails)]));
    const normalizeValue = (val: any): any => {
      if (val === undefined || val === null || val === "") return undefined;
      if (typeof val === "number") return val;
      if (typeof val === "string") return val.trim() || undefined;
      return val;
    };
    for (const key of allDetailKeys) {
      const cv = normalizeValue(currentDetails[key]);
      const iv = normalizeValue(initialDetails[key]);
      // Keys only in initial (e.g. filtered out by category's enabledDetails) — treat as no change
      if (iv !== undefined && cv === undefined) continue;
      // Treat 5 and "5" as equal (number vs string from Firestore)
      const detailsEqual = cv === iv || (
        (typeof cv === "number" || (typeof cv === "string" && cv !== "")) &&
        (typeof iv === "number" || (typeof iv === "string" && iv !== "")) &&
        !isNaN(parseFloat(String(cv))) && !isNaN(parseFloat(String(iv))) &&
        Math.abs(parseFloat(String(cv)) - parseFloat(String(iv))) < 1e-9
      );
      if (!detailsEqual) return true;
    }
    return false;
  }, [isEditMode, editingTransaction, initialFormData, initialTransferFormData, formData, transferFormData]);

  // Transaction passed from list when user taps card (avoids ~5s API wait on first open after hard refresh)
  const transactionFromState = (location.state as { transaction?: Transaction })?.transaction;
  const initialTransaction =
    isEditMode && transactionId && transactionFromState && transactionFromState.id === transactionId
      ? transactionFromState
      : undefined;

  // Use mobile edit mode hook
  useMobileEditMode({
    isEditMode,
    transactionId,
    workspaceId,
    workspaceSlug,
    transactionType,
    accounts,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    defaultCurrency,
    editingTransaction,
    setEditingTransaction,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    setSelectedCategoryDetails,
    setCalendarViewDate,
    setTimeWasEnabled,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    setInitialFormData,
    setInitialTransferFormData,
    setCalculatorExpression,
    calculateTransferRate,
    formatDateLocal,
    formatTimeLocal,
    navigate,
    setShowTransferFee,
    transactions,
    initialTransaction,
    transactionCustomFields: transactionType === "transfer" ? transferCustomFields : transactionType === "income" ? incomeCustomFields : expenseCustomFields,
  });

  // Don't resync rate into initialFormData when editing so "Update rate" keeps Update button enabled.
  // Fallback: resync transfer rate only after delay (catches late effects).
  useEffect(() => {
    if (!isEditMode || !editingTransaction || !initialTransferFormData) return;
    if (editingTransaction.type !== "transfer") return;
    const t = setTimeout(() => {
      const transfer = transferFormDataRef.current;
      setInitialTransferFormData((prev) => prev ? { ...prev, transferRate: transfer.transferRate } : null);
    }, 1200);
    return () => clearTimeout(t);
  }, [isEditMode, editingTransaction?.id, editingTransaction?.type]);

  // Handle duplicate transaction from location.state
  useEffect(() => {
    const duplicateTransactionId = (location.state as any)?.duplicateTransactionId;
    if (!duplicateTransactionId || isEditMode || !workspaceId || loading || !defaultCurrency) return;
    
    // Don't reload if we already have the transaction loaded
    if (editingTransaction && editingTransaction.id === duplicateTransactionId) return;

    const loadDuplicateTransaction = async () => {
      try {
        const response = await api.makeAuthenticatedRequest(
          `/workspaces/${workspaceId}/transactions/${duplicateTransactionId}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error("Failed to load transaction");
        }

        const transaction = await response.json();
        
        // Verify transaction type matches URL type
        const actualType = transaction.type === "transfer" 
          ? "transfer" 
          : transaction.isIncome 
            ? "income" 
            : "expense";
        
        if (actualType !== transactionType) {
          // Redirect to correct type
          const returnUrl = (location.state as any)?.returnUrl;
          navigate(`/${workspaceSlug}/create-transaction/${actualType}`, {
            replace: true,
            state: { duplicateTransactionId, returnUrl }
          });
          return;
        }

        // Helper function to parse date (same as useMobileEditMode)
        const parseTransactionDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (dateValue && typeof dateValue.toDate === 'function') return dateValue.toDate();
          if (dateValue._seconds) return new Date(dateValue._seconds * 1000);
          if (dateValue.seconds) return new Date(dateValue.seconds * 1000);
          if (typeof dateValue === 'string') return new Date(dateValue);
          if (dateValue instanceof Date) return dateValue;
          return new Date();
        };

        if (transaction.type === "transfer") {
          // Handle transfer duplicate
          const transferDate = parseTransactionDate(transaction.date);
          setTransferFormData({
            fromAccountId: transaction.fromAccountId || "",
            toAccountId: transaction.toAccountId || "",
            amount: transaction.amount?.toString() || "",
            date: formatDateLocal(new Date()),
            time: "",
            transferRate: transaction.transferRate?.toString() || "",
            customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
              transferCustomFields,
              "transfer",
              (transaction as any).customFieldValues,
              transaction.note,
              transaction.attachmentUrl ?? null,
              transaction.attachmentName ?? null,
              transaction.attachmentType ?? null
            ),
            customFieldPendingFiles: undefined,
          });
        } else {
          // Handle expense/income duplicate
          const accountId = transaction.accountId || "";
          const account = accountId ? accounts.find(a => a.id === accountId) : undefined;
          const transactionDate = parseTransactionDate(transaction.date);
          const transactionTime = transaction.time ? formatTimeLocal(transaction.time) : "";
          
          // Get currency and rate
          const transactionCurrency = transaction.currency || defaultCurrency;
          const existingRate = transaction.rate?.toString() || "";
          let existingDefaultCurrencyRate = transaction.defaultCurrencyRate?.toString() || "";
          // Stored rate 1 means default matched transaction currency at save time; if default has changed, use current rate (leave empty for auto-fill)
          if (existingDefaultCurrencyRate && transactionCurrency !== defaultCurrency) {
            const rateVal = parseFloat(existingDefaultCurrencyRate);
            if (!isNaN(rateVal) && Math.abs(rateVal - 1) < 1e-6) existingDefaultCurrencyRate = "";
          }
          
          // Get category name
          const categoryName = transaction.category || "";
          
          // Load category details if category exists
          const categories = transaction.isIncome ? incomeCategories : expenseCategories;
          const selectedCategory = categories.find((cat) => cat.name === categoryName);
          const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
          const allGroupDetails = group?.details || [];
          const enabledDetailNames = selectedCategory?.enabledDetails && Array.isArray(selectedCategory.enabledDetails)
            ? selectedCategory.enabledDetails 
            : [];
          const details = enabledDetailNames.length > 0 
            ? allGroupDetails.filter(detail => enabledDetailNames.includes(detail.name))
            : [];
          
          setSelectedCategoryDetails(details);
          
          const dupScope = actualType === "income" ? "income" : "expense";
          const dupCustomFields = actualType === "income" ? incomeCustomFields : expenseCustomFields;
          const duplicateFormData: FormData = {
            category: categoryName,
            amount: transaction.amount?.toString() || "",
            currency: transactionCurrency,
            rate: existingRate,
            defaultCurrencyRate: existingDefaultCurrencyRate,
            date: formatDateLocal(new Date()),
            time: "",
            accountId: accountId,
            accountGroup: account ? account.group : "",
            details: transaction.details,
            customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
              dupCustomFields,
              dupScope,
              (transaction as any).customFieldValues,
              transaction.note,
              transaction.attachmentUrl ?? null,
              transaction.attachmentName ?? null,
              transaction.attachmentType ?? null
            ),
            customFieldPendingFiles: undefined,
          };
          
          setFormData(duplicateFormData);
          setInitialFormData(duplicateFormData);
        }
        
        // Mark transaction as loaded to prevent reloading
        setEditingTransaction(transaction);
      } catch (error) {
        console.error("Error loading duplicate transaction:", error);
        alert("Failed to load transaction for duplication.");
      }
    };

    loadDuplicateTransaction();
  }, [location.state, isEditMode, workspaceId, loading, defaultCurrency, transactionType, workspaceSlug, navigate, accounts, expenseCategories, incomeCategories, categoryGroups, editingTransaction, setFormData, setTransferFormData, setSelectedCategoryDetails, setInitialFormData, setEditingTransaction, formatDateLocal, formatTimeLocal]);

  // Fee transaction (transfer fee or expense fee) when editing
  const feeTransaction = editingTransaction &&
    editingTransaction.type === "expense" &&
    ((editingTransaction.title === "Transfer Fee" || editingTransaction.category === "Transfer Fee") && (editingTransaction as any).transferFeeFor ||
     (editingTransaction.title === "Expense Fee" || editingTransaction.category === "Expense Fee") && (editingTransaction as any).expenseFeeFor)
    ? (editingTransaction as TransactionWithDate)
    : null;
  const isFeeEditMode = isEditMode && !!feeTransaction;
  const transferTransaction = feeTransaction && (feeTransaction as any).transferFeeFor
    ? (transactions.find((t: Transaction) => t.id === (feeTransaction as any).transferFeeFor && t.type === "transfer") as TransactionWithDate | undefined) ?? null
    : null;
  const expenseTransaction = feeTransaction && (feeTransaction as any).expenseFeeFor
    ? (transactions.find((t: Transaction) => t.id === (feeTransaction as any).expenseFeeFor && (t.type === "expense" || t.type === "income")) as TransactionWithDate | undefined) ?? null
    : null;

  // Stub functions for transaction history (mobile doesn't use transaction history)
  const saveTransactionHistory = useCallback(async (
    transactionId: string,
    oldTransaction: TransactionWithDate,
    newTransactionData: any
  ): Promise<void> => {
    // Mobile doesn't track transaction history, so this is a no-op
  }, []);

  const setTransactionHasHistory = useCallback((updater: (prev: Set<string>) => Set<string>) => {
    // Mobile doesn't track transaction history, so this is a no-op
  }, []);

  // Helper function to get account name
  const getAccountName = useCallback((accountId: string | undefined, storedAccountName?: string): string => {
    if (storedAccountName) return storedAccountName;
    const account = accounts.find(acc => acc.id === accountId);
    return account?.accountName || "Unknown Account";
  }, [accounts]);

  // Fee submission handler
  const navigateBackFromFeeEdit = useCallback(() => {
    const returnUrl = (location.state as any)?.returnUrl;
    if (returnUrl && workspaceSlug) {
      navigate(returnUrl, { replace: true });
    } else if (workspaceSlug) {
      navigate(`/${workspaceSlug}/activity`, { replace: true });
    }
  }, [workspaceSlug, navigate, location.state]);

  const { handleFeeSubmit } = useFeeSubmission({
    workspaceId,
    feeTransaction: feeTransaction || ({} as TransactionWithDate),
    feeFormData: feeEditFormData,
    accounts,
    defaultCurrency,
    isSubmittingFee,
    setIsSubmittingFee,
    setFeeEditDialogOpen,
    setEditingTransaction,
    setTransferFormData,
    setShowTransferFee,
    setSnackbarElement: () => {},
    setSnackbarOpen: () => {},
    saveTransactionHistory,
    setTransactionHasHistory,
    getAccountName,
    onSuccess: navigateBackFromFeeEdit,
  });

  // When editing a fee transaction, initialize fee form data (full-screen fee edit, no dialog).
  // Track which fee we initialized so we don't show "Update" as enabled before init (mobile only).
  const feeFormInitializedForIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (feeTransaction && isFeeEditMode) {
      setFeeEditFormData({
        feeAmount: feeTransaction.amount?.toString() || "",
        feeAccountId: feeTransaction.accountId || "",
      });
      feeFormInitializedForIdRef.current = feeTransaction.id;
    } else {
      feeFormInitializedForIdRef.current = null;
    }
  }, [feeTransaction?.id, isFeeEditMode]);


  // Use mobile field focus effects hook
  useMobileFieldFocusEffects({
    transactionType,
    loading,
    accounts,
    isInitialFocus,
    setIsInitialFocus,
    formData,
    transferFormData,
    focusedField,
    setFocusedField,
    calculatorExpression,
    setCalculatorExpression,
    hasEvaluatedRef,
    isOnOptionalField,
    amountInputRef,
    handleFieldFocus,
    getCalculatorDisplayValue,
    parseExpressionIntoCalculator,
    setCalendarViewDate,
    bottomPanelRef,
    timePickerRef,
    isEditMode,
    editingTransaction,
  });

  // getCurrencyRate, calculateTransferRate, and handleRefreshRate are now provided by useMobileRateCalculations hook
  // Auto-update rate logic is also handled by the hook

  // handleFieldFocus and handleFieldBlur are now provided by useMobileFieldFocus hook

  // Use mobile time picker effects hook
  useMobileTimePickerEffects({
    transactionType,
    isEditMode,
    editingTransaction,
    focusedField,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    timeWasEnabled,
    getCurrentTime,
    setCenteredHour,
    setCenteredMinute,
  });


  // Form handlers are now provided by useMobileFormHandlers hook

  // Form submission logic is now provided by useMobileTransactionSubmission hook


  // Use mobile calendar handlers hook
  const {
    handleCalendarDateSelect,
    handleCalendarMonthChange,
  } = useMobileCalendar({
    transactionType,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    calendarViewDate,
    setCalendarViewDate,
    keepTimePickerOpen,
    setFocusedField,
    formatDateLocal,
  });

  // Use mobile calculator handler hook
  const { handleCalculatorCheck } = useMobileCalculatorHandler({
    transactionType,
    focusedField,
    formData,
    setFormData,
    transferFormData,
    setTransferFormData,
    setFocusedField,
    setCalculatorExpression,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    hasEvaluatedRef,
    handleFieldFocus,
    currencies,
    defaultCurrency,
    accounts,
    isFeeEditMode,
    feeEditFormData,
    setFeeEditFormData,
  });

  // Use mobile time picker handlers hook
  const { handleTimePickerDone } = useMobileTimePickerHandlers({
    transactionType,
    formData,
    transferFormData,
    setFocusedField: (field: FocusedField) => setFocusedField(field),
    setKeepTimePickerOpen,
    isOnOptionalField,
    handleFieldFocus,
  });

  // Get filtered accounts for selection (using utility function)
  const filteredAccounts = getFilteredAccounts(
    accounts,
    transactionType,
    focusedField,
    transferFormData.fromAccountId
  );

  // When fee account is focused: fee edit form, expense fee, or transfer fee
  const handleAccountSelectForPanel = useCallback((accountId: string) => {
    if (isFeeEditMode && focusedField === "feeAccount") {
      setFeeEditFormData((prev) => ({ ...prev, feeAccountId: accountId }));
      setFocusedField(null);
    } else if (!isFeeEditMode && focusedField === "feeAccount" && transactionType === "expense") {
      setFormData((prev) => ({ ...prev, feeAccountId: accountId }));
      setTimeout(() => {
        const hasAmount = formData.amount != null && formData.amount.trim() !== "" && formData.amount.trim() !== "0";
        const hasAccount = formData.accountId != null && formData.accountId.trim() !== "";
        const hasCategory = formData.category != null && formData.category.trim() !== "";
        if (!hasAmount) handleFieldFocus("amount");
        else if (!hasAccount) handleFieldFocus("account");
        else if (!hasCategory) handleFieldFocus("category");
        else setFocusedField(null);
      }, 0);
    } else if (!isFeeEditMode && focusedField === "feeAccount" && transactionType === "transfer") {
      setTransferFormData((prev) => ({ ...prev, feeAccountId: accountId }));
      setTimeout(() => {
        const hasAmount = transferFormData.amount != null && transferFormData.amount.trim() !== "" && transferFormData.amount.trim() !== "0";
        const hasFrom = transferFormData.fromAccountId != null && transferFormData.fromAccountId.trim() !== "";
        const hasTo = transferFormData.toAccountId != null && transferFormData.toAccountId.trim() !== "";
        if (!hasAmount) handleFieldFocus("amount");
        else if (!hasFrom) handleFieldFocus("fromAccount");
        else if (!hasTo) handleFieldFocus("toAccount");
        else setFocusedField(null);
      }, 0);
    } else {
      handleAccountSelect(accountId);
    }
  }, [isFeeEditMode, focusedField, transactionType, setFeeEditFormData, setFormData, setTransferFormData, setFocusedField, handleAccountSelect, handleFieldFocus, formData.amount, formData.accountId, formData.category, transferFormData.amount, transferFormData.fromAccountId, transferFormData.toAccountId]);

  // Get current categories (using utility function)
  const currentCategories = getCurrentCategories(
    transactionType,
    expenseCategories,
    incomeCategories
  );

  // In edit mode, show the form immediately so the edit page opens without waiting for
  // accounts/categories to load; the transaction loads via useMobileEditMode and data streams in.
  if (loading && !isEditMode) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      {/* Header - Always visible, including when amount/date is focused */}
      <MobileTransactionHeader
        transactionType={transactionType}
        isEditMode={isEditMode}
        workspaceSlug={workspaceSlug}
        addOptionDialogOpen={addOptionDialogOpen}
        routeStorageKey={routeStorageKey}
        transactionIdPrefix={(dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? ""}
        friendlyIdSequence={editingTransaction?.friendlyIdSequence}
        nextFriendlyIdSequence={dataFetchingTransactionIdNextSequence ?? transactionIdNextSequence}
        transactionId={editingTransaction?.id}
        workspaceMembersCount={workspaceMembersCount}
        titleOverride={isFeeEditMode ? "Edit Fee" : undefined}
        onBackClick={() => {
          // Mark that we're intentionally navigating back FIRST
          isNavigatingBackRef.current = true;
          
          // Clear route storage key to prevent Activity from auto-redirecting back
          try {
            sessionStorage.removeItem(routeStorageKey);
          } catch (error) {
            console.error("Error clearing route info:", error);
          }
          
          // Check if we have a returnUrl in location.state (from CategoryGroupDetail)
          const returnUrl = (location.state as any)?.returnUrl;
          if (returnUrl && workspaceSlug) {
            // Navigate back to the graph page
            navigate(returnUrl, { replace: true });
          } else if (workspaceSlug) {
            // Navigate to Activity page (use replace to prevent back button from going to transaction page)
            navigate(`/${workspaceSlug}/activity`, { replace: true });
          } else {
            console.error("workspaceSlug is missing, cannot navigate");
          }
        }}
        onClearNewlyAddedOptions={() => setNewlyAddedDetailOptions({})}
        centerContent={!isFeeEditMode && !isEditMode && workspaceSlug ? (
          <Tabs
            value={transactionType === "expense" ? 0 : transactionType === "transfer" ? 1 : 2}
            onChange={(_, newValue) => {
              const typeMap = ["expense", "transfer", "income"] as const;
              navigate(`/${workspaceSlug}/create-transaction/${typeMap[newValue]}`, { replace: true });
            }}
            sx={{
              minHeight: 40,
              width: "100%",
              "& .MuiTabs-flexContainer": { width: "100%" },
              "& .MuiTab-root": { minHeight: 40, py: 1, fontSize: "0.875rem", flex: 1 },
              "& .MuiTab-root.Mui-selected": {
                color:
                  transactionType === "expense"
                    ? "error.main"
                    : transactionType === "income"
                      ? "success.main"
                      : "info.main",
              },
              "& .MuiTabs-indicator": {
                backgroundColor:
                  transactionType === "expense"
                    ? "error.main"
                    : transactionType === "income"
                      ? "success.main"
                      : "info.main",
              },
            }}
          >
            <Tab label="Expenses" />
            <Tab label="Transfer" />
            <Tab label="Income" />
          </Tabs>
        ) : undefined}
      />

      {/* Form Content - Scrollable */}
      <Box
        ref={formContentRef}
        sx={{
          flex: 1,
          overflowY: (focusedField === "date" || focusedField === "time") ? "hidden" : "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
          pt: 2, // Keep consistent top padding so title stays visible
          px: 2,
          pb: (focusedField === "amount" || focusedField === "rate" || focusedField === "defaultCurrencyRate" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"))) ? "380px" : (focusedField === "date" || focusedField === "time") ? "400px" : focusedField ? "300px" : "calc(80px + env(safe-area-inset-bottom, 0px))", // Space for fixed Cancel/Update bar when no panel open
          width: "100%",
          maxWidth: "100%",
          pointerEvents: addOptionDialogOpen ? "none" : "auto",
          opacity: addOptionDialogOpen ? 0.5 : 1,
          transition: "opacity 0.2s ease",
        }}
      >
        {isFeeEditMode ? (
          <form onSubmit={handleFeeSubmit} id="fee-edit-form" style={{ width: "100%", maxWidth: "100%", overflowX: "hidden", height: "100%", paddingTop: 5 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Related Transfer or Expense (read-only) */}
              {transferTransaction && (() => {
                const fromAccount = transferTransaction.fromAccountId ? accounts.find((acc) => acc.id === transferTransaction.fromAccountId && !acc.isArchived) : null;
                const toAccount = transferTransaction.toAccountId ? accounts.find((acc) => acc.id === transferTransaction.toAccountId && !acc.isArchived) : null;
                const transferDate = transferTransaction.date ? formatDateLocal(transferTransaction.date instanceof Date ? transferTransaction.date : new Date((transferTransaction.date as any)?.seconds ? (transferTransaction.date as any).seconds * 1000 : transferTransaction.date)) : "";
                return (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, border: 1, borderColor: "divider" }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>Related Transfer</Typography>
                    <Stack spacing={1}>
                      {fromAccount && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">From Account</Typography>
                          <Typography variant="body2">
                            {fromAccount.accountName} ({formatCurrencyUtil(fromAccount.amount ?? 0, fromAccount.currency, getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency))} {fromAccount.currency})
                          </Typography>
                        </Box>
                      )}
                      {toAccount && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">To Account</Typography>
                          <Typography variant="body2">
                            {toAccount.accountName} ({formatCurrencyUtil(toAccount.amount ?? 0, toAccount.currency, getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency))} {toAccount.currency})
                          </Typography>
                        </Box>
                      )}
                      {transferTransaction.amount !== undefined && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Transfer Amount</Typography>
                          <Typography variant="body2">
                            {formatCurrencyUtil(transferTransaction.amount, transferTransaction.currency ?? defaultCurrency, getCurrencyDecimalPlaces(transferTransaction.currency ?? defaultCurrency, currencies, defaultCurrency))}
                          </Typography>
                        </Box>
                      )}
                      {transferDate && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Date</Typography>
                          <Typography variant="body2">{transferDate}</Typography>
                        </Box>
                      )}
                      {(getTransactionFriendlyId((dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? "", transferTransaction.friendlyIdSequence) || transferTransaction.id) && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">ID</Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.8125rem", minWidth: 0 }}>
                            {getTransactionFriendlyId((dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? "", transferTransaction.friendlyIdSequence) ?? transferTransaction.id}
                          </Typography>
                          <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`mobile-fee-transfer-id-${transferTransaction.id}`} lastCopiedKey={feeEditCopyLastKey} isMobile={true}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const id = getTransactionFriendlyId((dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? "", transferTransaction.friendlyIdSequence) ?? transferTransaction.id;
                                copyToClipboard(id).then((ok) => ok && showFeeEditCopyFeedback(`mobile-fee-transfer-id-${transferTransaction.id}`));
                              }}
                              sx={{ p: 0.25 }}
                              aria-label="Copy transaction ID"
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </CopyFeedbackTooltip>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                );
              })()}
              {expenseTransaction && !transferTransaction && (
                <Paper elevation={0} sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, border: 1, borderColor: "divider" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">Related Expense</Typography>
                    {(getTransactionFriendlyId((dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? "", expenseTransaction.friendlyIdSequence) || expenseTransaction.id) && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                          ID: {getTransactionFriendlyId((dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? "", expenseTransaction.friendlyIdSequence) ?? expenseTransaction.id}
                        </Typography>
                        <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={`mobile-fee-expense-id-${expenseTransaction.id}`} lastCopiedKey={feeEditCopyLastKey} isMobile={true}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const id = getTransactionFriendlyId((dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? "", expenseTransaction.friendlyIdSequence) ?? expenseTransaction.id;
                              copyToClipboard(id).then((ok) => ok && showFeeEditCopyFeedback(`mobile-fee-expense-id-${expenseTransaction.id}`));
                            }}
                            sx={{ p: 0.25 }}
                            aria-label="Copy transaction ID"
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </CopyFeedbackTooltip>
                      </Box>
                    )}
                  </Box>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Category</Typography>
                      <Typography variant="body2">{expenseTransaction.category ?? expenseTransaction.title ?? ""}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">
                        {formatCurrencyUtil(expenseTransaction.amount ?? 0, expenseTransaction.currency ?? defaultCurrency, getCurrencyDecimalPlaces(expenseTransaction.currency ?? defaultCurrency, currencies, defaultCurrency))}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Date</Typography>
                      <Typography variant="body2">{expenseTransaction.date ? formatDateLocal(expenseTransaction.date instanceof Date ? expenseTransaction.date : new Date((expenseTransaction.date as any)?.seconds ? (expenseTransaction.date as any).seconds * 1000 : expenseTransaction.date)) : ""}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
              <Divider />
              <Typography variant="subtitle2" fontWeight="bold">Fee Details</Typography>
              <TextField
                required
                label="Fee Amount"
                value={focusedField === "feeAmount" && calculatorExpression !== null && calculatorExpression !== undefined && calculatorExpression.trim() !== "" ? calculatorExpression : (feeEditFormData.feeAmount || "")}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (focusedField !== "feeAmount") { setCalculatorExpression(feeEditFormData.feeAmount === "" ? "0" : feeEditFormData.feeAmount); handleFieldFocus("feeAmount"); } }}
                onFocus={(e) => { e.preventDefault(); (e.target as HTMLInputElement).blur(); if (focusedField !== "feeAmount") { setCalculatorExpression(feeEditFormData.feeAmount === "" ? "0" : feeEditFormData.feeAmount); handleFieldFocus("feeAmount"); } }}
                fullWidth
                placeholder="0.00"
                tabIndex={-1}
                inputProps={{ inputMode: "none" }}
                InputProps={{
                  endAdornment: feeEditFormData.feeAccountId ? (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary">{accounts.find((acc) => acc.id === feeEditFormData.feeAccountId)?.currency ?? defaultCurrency}</Typography>
                    </InputAdornment>
                  ) : undefined,
                  readOnly: true,
                }}
                sx={{ "& .MuiInputBase-input": { cursor: "pointer" } }}
              />
              <TextField
                required
                label="Fee Account"
                value={feeEditFormData.feeAccountId ? (() => { const acc = accounts.find((a) => a.id === feeEditFormData.feeAccountId && !a.isArchived); return acc ? `${acc.accountName} (${formatCurrencyUtil(acc.amount ?? 0, acc.currency, getCurrencyDecimalPlaces(acc.currency, currencies, defaultCurrency))} ${acc.currency})` : ""; })() : ""}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleFieldFocus("feeAccount"); }}
                onFocus={(e) => { e.preventDefault(); (e.target as HTMLInputElement).blur(); handleFieldFocus("feeAccount"); }}
                fullWidth
                tabIndex={-1}
                inputProps={{ inputMode: "none" }}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-input": { cursor: "pointer" } }}
              />
            </Stack>
          </form>
        ) : (
        <form onSubmit={handleSubmit} id="transaction-form" style={{ width: "100%", maxWidth: "100%", overflowX: "hidden", height: "100%", paddingTop: 5 }}>
          <MobileTransactionFormFields
            transactionType={transactionType}
            isEditMode={isEditMode}
            editingTransaction={editingTransaction}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            formData={formData}
            setFormData={setFormData}
            transferFormData={transferFormData}
            setTransferFormData={setTransferFormData}
            accounts={accounts}
            currencies={currencies}
            defaultCurrency={defaultCurrency}
            selectedCategoryDetails={selectedCategoryDetails}
            newlyAddedDetailOptions={newlyAddedDetailOptions}
            setNewlyAddedDetailOptions={setNewlyAddedDetailOptions}
            openDetailLists={openDetailLists}
            setOpenDetailLists={setOpenDetailLists}
            addOptionDialogOpen={addOptionDialogOpen}
            setAddOptionDialogDetailName={setAddOptionDialogDetailName}
            setAddOptionDialogValue={setAddOptionDialogValue}
            setAddOptionDialogError={setAddOptionDialogError}
            setAddOptionDialogOpen={setAddOptionDialogOpen}
            uploadingAttachment={uploadingAttachment}
            isOnOptionalField={isOnOptionalField}
            setIsOnOptionalField={setIsOnOptionalField}
            keepTimePickerOpen={keepTimePickerOpen}
            setKeepTimePickerOpen={setKeepTimePickerOpen}
            rateManuallyEdited={rateManuallyEdited}
            defaultCurrencyRateManuallyEdited={defaultCurrencyRateManuallyEdited}
            calculateTransferRate={calculateTransferRate}
            handleRefreshRate={handleRefreshRate}
            handleRefreshDefaultCurrencyRate={handleRefreshDefaultCurrencyRate}
            handleFieldFocus={handleFieldFocus}
            handleFieldBlur={handleFieldBlur}
            handleSubmit={handleSubmit}
            formatDateForDisplay={formatDateForDisplay}
            getCurrentTime={getCurrentTime}
            timeFormat={timeFormat}
            getCalculatorDisplayValue={getCalculatorDisplayValue}
            parseExpressionIntoCalculator={parseExpressionIntoCalculator}
            setCalculatorExpression={setCalculatorExpression}
            setCalculatorResetKey={setCalculatorResetKey}
            amountInputRef={amountInputRef}
            customFieldFileInputRef={customFieldFileInputRef}
            transferCustomFieldFileInputRef={transferCustomFieldFileInputRef}
            expenseCustomFields={expenseCustomFields}
            incomeCustomFields={incomeCustomFields}
            transferCustomFields={transferCustomFields}
            handleAccountSelect={handleAccountSelect}
            handleCurrencySelect={handleCurrencySelect}
            hookHandleCategorySelect={hookHandleCategorySelect}
            checkDuplicateOption={checkDuplicateOption}
            handleAddDetailOption={handleAddDetailOption}
            handleCloseAddOptionDialog={handleCloseAddOptionDialog}
            addOptionDialogValue={addOptionDialogValue}
            addOptionDialogDetailName={addOptionDialogDetailName}
            addOptionDialogError={addOptionDialogError}
            isIOS={isIOS}
            showTransferFee={showTransferFee}
            setShowTransferFee={setShowTransferFee}
            allowExpenseFee={allowExpenseFee}
            allowTransferFee={allowTransferFee}
          />
        </form>
        )}
      </Box>

      {/* Time Picker */}
      <MobileTimePicker
        open={focusedField === "time" || (focusedField === "date" && keepTimePickerOpen)}
        currentTime={transactionType === "transfer" ? transferFormData.time : formData.time}
        isEditMode={isEditMode}
        timeWasEnabled={timeWasEnabled}
        transactionType={transactionType}
        onTimeChange={(time: string) => {
          if (transactionType === "transfer") {
            setTransferFormData((prev) => ({ ...prev, time }));
          } else {
            setFormData((prev) => ({ ...prev, time }));
          }
        }}
        onClose={() => {
          setFocusedField(null);
          setKeepTimePickerOpen(false);
        }}
        onDone={handleTimePickerDone}
        getCurrentTime={getCurrentTime}
        timeFormat={timeFormat}
      />

      {/* Date Picker */}
      <MobileDatePicker
        open={focusedField === "date"}
        selectedDate={transactionType === "transfer" ? transferFormData.date : formData.date}
        viewDate={calendarViewDate}
        keepTimePickerOpen={keepTimePickerOpen}
        isIOS={isIOS}
        onDateSelect={handleCalendarDateSelect}
        onMonthChange={handleCalendarMonthChange}
        onViewDateChange={setCalendarViewDate}
        formatDateLocal={formatDateLocal}
      />

      {/* Calculator */}
      <MobileCalculator
        key={`calc-${calculatorResetKey}`}
        open={focusedField === "amount" || focusedField === "rate" || focusedField === "defaultCurrencyRate" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"))}
        initialValue={
          (() => {
            // Check field type first (before isOpen check) to avoid TypeScript narrowing issues
            const isRateField = focusedField === "rate";
            const isAmountField = focusedField === "amount";
            const isDefaultCurrencyRateField = focusedField === "defaultCurrencyRate";
            const isFeeAmountField = focusedField === "feeAmount";
            const isDetailField = typeof focusedField === "string" && focusedField.startsWith("detail:");
            const detailName = isDetailField ? focusedField.slice(7) : "";
            const isOpen = isAmountField || isRateField || isDefaultCurrencyRateField || isFeeAmountField || isDetailField;
            
            // When calculator is open, use calculatorExpression for amount/rate/fee (not for detail - use formData so we show the right value when switching)
            let calculatedValue: string;
            if (isDetailField && detailName) {
              const v = formData.details?.[detailName];
              calculatedValue = v === undefined || v === null || v === "" ? "0" : String(v);
            } else if (isOpen && calculatorExpression !== null && calculatorExpression !== undefined && calculatorExpression.trim() !== "") {
              calculatedValue = calculatorExpression;
            } else if (isOpen) {
              calculatedValue = "0";
            } else {
              // Calculator is closed, calculate from formData
              // Determine which field we're calculating for based on focusedField
              if (isRateField) {
                calculatedValue = formData.rate || "0";
              } else if (isDefaultCurrencyRateField) {
                calculatedValue = formData.defaultCurrencyRate || "0";
              } else if (isFeeAmountField) {
                // If fee amount is empty or "0", show "0" in calculator (fee edit / transfer / expense)
                if (isFeeEditMode && feeEditFormData.feeAmount && feeEditFormData.feeAmount !== "0") {
                  calculatedValue = convertFormattedAmountToRawValue(feeEditFormData.feeAmount);
                } else if (!isFeeEditMode && transactionType === "transfer" && transferFormData.feeAmount && transferFormData.feeAmount !== "0") {
                  calculatedValue = convertFormattedAmountToRawValue(transferFormData.feeAmount);
                } else if (!isFeeEditMode && transactionType === "expense" && formData.feeAmount && formData.feeAmount !== "0") {
                  calculatedValue = convertFormattedAmountToRawValue(formData.feeAmount);
                } else {
                  calculatedValue = "0";
                }
              } else if (transactionType === "transfer") {
                // If amount is empty or "0", show "0" in calculator
                calculatedValue = transferFormData.amount && transferFormData.amount !== "0" 
                  ? convertFormattedAmountToRawValue(transferFormData.amount) 
                  : "0";
              } else {
                // If amount is empty or "0", show "0" in calculator
                calculatedValue = formData.amount && formData.amount !== "0" 
                  ? convertFormattedAmountToRawValue(formData.amount) 
                  : "0";
              }
            }
            return calculatedValue;
          })()
        }
        onValueChange={(value) => {
          // Mark rate as manually edited as soon as user types so refresh button enables immediately
          // Also update formData immediately so auto-populate doesn't restore the old value while user is deleting
          if (focusedField === "rate") {
            setRateManuallyEdited(true);
            // Update formData immediately when user types/deletes so auto-populate sees the updated value
            const cleanValue = value === "0" ? "" : value;
            setFormData((prev) => ({ ...prev, rate: cleanValue }));
          } else if (focusedField === "defaultCurrencyRate") {
            setDefaultCurrencyRateManuallyEdited(true);
            // Update formData immediately when user types/deletes so auto-populate sees the updated value
            const cleanValue = value === "0" ? "" : value;
            setFormData((prev) => ({ ...prev, defaultCurrencyRate: cleanValue }));
          } else if (focusedField === "feeAmount") {
            const cleanValue = value === "0" ? "0" : value;
            if (isFeeEditMode) {
              setFeeEditFormData((prev) => ({ ...prev, feeAmount: cleanValue }));
            } else if (transactionType === "transfer") {
              setTransferFormData((prev) => ({ ...prev, feeAmount: cleanValue }));
            } else {
              setFormData((prev) => ({ ...prev, feeAmount: cleanValue }));
            }
          } else if (typeof focusedField === "string" && focusedField.startsWith("detail:")) {
            const detailName = focusedField.slice(7);
            const toStore = value === "" || value === "0" ? "" : (parseFloat(value) || 0);
            setFormData((prev) => ({
              ...prev,
              details: { ...(prev.details || {}), [detailName]: toStore },
            }));
          } else if (focusedField === "amount") {
            // Update amount immediately when user types/deletes so original value doesn't reappear (X button clears correctly)
            const cleanValue = value === "0" ? "0" : value;
            if (transactionType === "transfer") {
              setTransferFormData((prev) => ({ ...prev, amount: cleanValue }));
            } else {
              setFormData((prev) => ({ ...prev, amount: cleanValue === "0" ? "" : cleanValue }));
            }
          }
          setCalculatorExpression(value);
        }}
        onCheck={handleCalculatorCheck}
        onClose={() => {
          if (focusedField === "feeAmount") {
            setFocusedField("feeAccount");
          } else {
            setFocusedField(null);
          }
        }}
      />

      {/* Bottom Options Panel */}
      <MobileBottomPanel
        open={!!(focusedField && focusedField !== "date" && focusedField !== "amount" && focusedField !== "time" && focusedField !== "rate" && focusedField !== "defaultCurrencyRate" && focusedField !== "feeAmount" && !(typeof focusedField === "string" && focusedField.startsWith("detail:")))}
        focusedField={focusedField && focusedField !== "date" && focusedField !== "amount" && focusedField !== "time" && focusedField !== "rate" && focusedField !== "defaultCurrencyRate" && focusedField !== "feeAmount" && !(typeof focusedField === "string" && focusedField.startsWith("detail:")) ? focusedField : null}
        accounts={filteredAccounts}
        selectedAccountId={formData.accountId}
        selectedFromAccountId={transferFormData.fromAccountId}
        selectedToAccountId={transferFormData.toAccountId}
        selectedFeeAccountId={isFeeEditMode ? feeEditFormData.feeAccountId : (transactionType === "transfer" ? transferFormData.feeAccountId : formData.feeAccountId)}
        onAccountSelect={handleAccountSelectForPanel}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        selectedCurrency={formData.currency}
        onCurrencySelect={handleCurrencySelect}
        categories={currentCategories}
        categoryGroups={categoryGroups}
        selectedCategory={formData.category}
        onCategorySelect={handleCategorySelect}
        formatCurrency={(amount, currency) => formatCurrencyUtil(amount, currency, getCurrencyDecimalPlaces(currency, currencies, defaultCurrency))}
      />

      {/* Submit Button - Hide when keyboard or any panel is open */}
      <MobileTransactionSubmitButton
        transactionType={transactionType}
        isEditMode={isEditMode}
        workspaceSlug={workspaceSlug}
        isSubmitting={isFeeEditMode ? isSubmittingFee : isSubmitting}
        uploadingAttachment={uploadingAttachment}
        focusedField={focusedField}
        isInitialFocus={isInitialFocus}
        addOptionDialogOpen={addOptionDialogOpen}
        submitFormId={isFeeEditMode ? "fee-edit-form" : "transaction-form"}
        onCancel={() => {
          const returnUrl = (location.state as any)?.returnUrl;
          if (returnUrl && workspaceSlug) {
            navigate(returnUrl, { replace: true });
          } else if (workspaceSlug) {
            navigate(`/${workspaceSlug}/activity`);
          }
        }}
        hasChanges={isFeeEditMode
          ? (feeFormInitializedForIdRef.current === feeTransaction?.id &&
             (feeEditFormData.feeAmount !== (feeTransaction?.amount?.toString() ?? "") || feeEditFormData.feeAccountId !== (feeTransaction?.accountId ?? "")))
          : hasTransactionChanges}
      />

      {/* Dialog for adding new detail option */}
      <MobileAddOptionDialog
        open={addOptionDialogOpen}
        value={addOptionDialogValue}
        detailName={addOptionDialogDetailName}
        error={addOptionDialogError}
        onClose={handleCloseAddOptionDialog}
        onValueChange={(newValue) => {
          setAddOptionDialogValue(newValue);
          // Live duplicate detection
          const error = checkDuplicateOption(newValue, addOptionDialogDetailName);
          setAddOptionDialogError(error);
        }}
        onAdd={handleAddDetailOption}
        checkDuplicateOption={checkDuplicateOption}
      />

      {/* Fee Edit Dialog (legacy; full-screen fee edit is used when navigating to this page for a fee) */}
      {feeTransaction && (
        <MobileFeeEditDialog
          open={feeEditDialogOpen}
          onClose={() => {
            setFeeEditDialogOpen(false);
            setEditingTransaction(null);
          }}
          onSubmit={handleFeeSubmit}
          formData={feeEditFormData}
          setFormData={setFeeEditFormData}
          feeTransaction={feeTransaction}
          transferTransaction={transferTransaction}
          expenseTransaction={expenseTransaction}
          transactionIdPrefix={(dataFetchingTransactionIdPrefix ?? transactionIdPrefix) ?? ""}
          accounts={accounts}
          currencies={currencies}
          defaultCurrency={defaultCurrency}
          isSubmitting={isSubmittingFee}
          isValid={!!(feeEditFormData.feeAmount && parseFloat(feeEditFormData.feeAmount) >= 0 && feeEditFormData.feeAccountId)}
          hasFeeChanges={feeEditFormData.feeAmount !== (feeTransaction?.amount?.toString() ?? "") || feeEditFormData.feeAccountId !== (feeTransaction?.accountId ?? "")}
          workspaceSlug={workspaceSlug}
        />
      )}
    </Box>
  );
}

export default CreateTransactionMobile;
